import { Fragment, useRef, useState, useEffect } from "react";
import "./NodeList.scss";
import { processGroupsToSubNodes } from "../v2/CustomisationWorkspace/v2IntegrationHelpers/VisualEditorDataFormatHelperFunctions";

/**
 * NodeList Component
 * A react component to display a list of nodes with support for drag-and-drop reordering, expanding/collapsing nodes, selection, and renaming.
 * @author Carl Trelfa
 * @date 2025-06-06
 * @version 1.0.0
 * 
 * Props:
 * - id: String (unique identifier for the node list, when accessing some functions via NODE_LIST_CONTROLLER).
 * - nodes: Array of node objects to display in the list.
 * - indentation: String representing the indentation for nested nodes (default: "10px").
 * - expandNodeElement: Element to display for expanding nodes (default: <span>+</span>).
 * - collapseNodeElement: Element to display for collapsing nodes (default: <span>-</span>).
 * - dragHandleElement: Element to display as the drag handle (default: <span>::</span>).
 * - dropIndicatorElement: Element to display as the drop indicator (default: <div className="node-list_drag-drop-indicator"></div>).
 * - autoCollapseChildren: Boolean to automatically collapse children when a node is collapsed (default: true).
 * - clickAnywhereToExpand: Boolean to allow clicking anywhere on the node to expand it (default: true).
 * - autoExpandSelectedChildren: Boolean to automatically expand nodes with selected children (default: true).
 * - showGhostWhileDragging: Boolean to show a ghost clone of the node while dragging (default: true).
 * - dragGhostOpacity: Number representing the opacity of the ghost clone while dragging (default: 0.75).
 * - dragMode: String representing the drag mode (default: NODE_LIST_DRAG_MODES.INDICATED_DRAG).
 * - highlightParentNodeOnHover: Boolean to highlight the parent node when hovering over a child (default: false).
 * - getSubNodes: Function to fetch sub-nodes for nodes that have children but no subNodes provided or are set to use dynamicSubNodes (default: console.log).
 * - renderNodeName: Function to render the node name, receives (nodeName, node, nodes), can return a React element for custom rendering.
 * - checkNodeVisibility: Function to determine if a node should be visible, receives (node, nodes), should return a boolean (default: undefined, will result in every list node being visible).
 * - onNodeClick: Function to handle node click events (default: console.log).
 * - onNodeDragEnd: Function to handle drag end events (default: console.log).
 * - enableRightClickMenu: Boolean to enable right-click context menu (default: true).
 * - onNodeRightClick: Function to handle right-click events (default: console.log).
 * - onNodeRenamed: Function to handle node renaming events, called after a node has been renamed using the internal functionality (default: console.log).
 *
 * Node Data Structure:
 * - Each node should have the following properties:
 *   id: String (unique identifier for the node)
 *   name: String (display name of the node)
 *   path: String (path of the node, used for identification)
 *   enabled: Boolean (whether the node is enabled)
 *   selected: Boolean (whether the node is selected)
 *   highlighted: Boolean (whether the node is highlighted)
 *   active: Boolean (whether the node is currently active, recommended to set this on right-click and unset when right click menu is closed)
 *   hasChildren: Boolean (whether the node has children)
 *   dynamicSubNodes: Boolean (whether the node has dynamic sub-nodes)
 *   expanded: Boolean (whether the node is expanded)
 *   subNodes: Array (array of sub-nodes, can be null if no sub-nodes)
 *   draggable: Boolean (whether the node is draggable)
 *   clickable: Boolean (whether the node is clickable)
 *   dynamicName: Object (optional, for dynamic node name resolution)
 *   dynamicLinkedIndex: Number (optional, used with dynamicName)
 *   parent: Reference to parent node (added internally for easier access)
 *   depth: Number (added internally, indicates nesting level)
 * 
 * You can include any additional properties you want in the node data structure, the node will be returned when clicked
 * so you can use those properties in your onNodeClick function.
 * 
 * Properties not included in the node data structure will be treated as false or empty.
 * 
 * hasChildren and dynamicSubNodes are used to determine if the node has children and if those children are dynamically fetched.
 * The getSubNodes function is used to fetch sub-nodes for nodes. 
 * If dynamicSubNodes is set, getSubNodes is called every render pass and gives you the opportunity to dynamically make changes to the node list. 
 * If dynamicSubNodes is false, the subNodes will only be fetched if none currently exist in the list, therefore, you can provide minimal 
 * levels of subnodes which can be fetched as needed, this is useful if generating the subnodes requires lots of processing (we don't have to do all that in one go).
 * If getSubNodes returns a falsy value, the node's current subNodes will be used.
 * 
 * Important Notes:
 * 
 * GetSubNodes will be called for the level BELOW the current node so we have the next level ready to display immediately.
 * 
 * When using dynamicSubNodes, new copies of the nodes are made on each render pass, so you should not directly modify the node state.
 * Instead, modify your data that you derive the nodes from, the NodeList component will request new sub-nodes from getSubNodes when it is ready.
 * Node data passed out of the node list should be treated as read-only and only used to identify the data you want to modify in your application state.
 * 
 * When setting dragMode, use NODE_LIST_DRAG_MODES.LIVE_DRAG for live reordering of nodes as you drag them, 
 * or NODE_LIST_DRAG_MODES.INDICATED_DRAG to indicate where the node will be dropped with a line.
 * 
 * The component supports right clicking but by default it will not do anything with the right click.
 * It is up to you to implement the right click context menu in the onNodeRightClick function.
 * Ensure you call the provided onClose callback when you are done to re-render the node list.
 * 
 * Additional Features:
 * - Supports renaming nodes in-place with auto-selected input.
 * - NODE_LIST_CONTROLLER provides external control for refreshing, renaming, and selecting nodes by id/path.
 * - highlightParentNodeOnHover prop allows highlighting the parent node when hovering over a child node.
 */

export const NODE_LIST_DRAG_MODES = {
  LIVE_DRAG: 'live-drag',           // Dragging nodes live in the list
  INDICATED_DRAG: 'indicated-drag', // Indicate where the node will be dropped with a line
}

/**
 * NODE_LIST_CONTROLLER
 * A controller for managing the node list from outside the component.
 * It provides functions to refresh the node list, rename nodes.
 * Nodes are always considered ranamable, but they must be visible in the list. Renaming is always triggered by the renameNode function.
 * The best strategy for renaming nodes is to trigger this function via a right click menu. The node will be renamed in the local data structure,
 * and the result passed to the onNodeRenamed function, therefore you may need to update your application state with the new node data.
 */
const nodeListControlFunctions = {};  // Private object to store control functions for each node list by id
export const NODE_LIST_CONTROLLER = {
  nodeListExists: (nodeListId) => {
    return !!nodeListControlFunctions[nodeListId];
  },
  // Refresh the node list, sometimes you may want to refresh the node list from outside the component.
  // This will call the getSubNodes function to fetch the latest sub-nodes and update the node list.
  refreshNodeList: (nodeListId) => {
    // console.log("NODE_LIST_CONTROLLER.refreshNodeList called for nodeListId:", nodeListId, nodeListControlFunctions);
    let nodeListsHaveUpdated = false;
    if (!nodeListControlFunctions[nodeListId]) {
      // Update all nodes who's id starts with the nodeListId
      Object.keys(nodeListControlFunctions).forEach(key => {
        if (key.startsWith(nodeListId) && nodeListControlFunctions[key] && nodeListControlFunctions[key].nodesUpdated) {
          nodeListControlFunctions[key].nodesUpdated();
          nodeListsHaveUpdated = true;
          // console.log(`NODE_LIST_CONTROLLER.refreshNodeList updated NodeList with id: ${key}`);
        }
      });
    }
    if (!nodeListsHaveUpdated) {
      if (nodeListControlFunctions[nodeListId] && nodeListControlFunctions[nodeListId].nodesUpdated) {
        nodeListControlFunctions[nodeListId].nodesUpdated();
      } else {
        console.warn(`NodeList with id ${nodeListId} not found. Cannot refresh node list.`);
      }
    }
  },
  // Rename node in the node list, causes a form field to appear in the list in place of the node's name to rename the node
  // Note: renaming nodes outside the list can be handled by just passing in the new updated list via props or 
  // using the getSubNodes function to fetch the updated list (calling refreshNodeList will cause getSubNodes to be called).
  renameNode: (nodeListId, nodeData) => {
    if (nodeListControlFunctions[nodeListId] && nodeListControlFunctions[nodeListId].renameNode) {
      if (nodeData.id && nodeData.path) {
        nodeListControlFunctions[nodeListId].renameNode(nodeData.id, nodeData.path);
      }
    } else {
      console.warn(`NodeList with id ${nodeListId} not found. Cannot rename node.`);
    }
  },
  // Select a node by id and path, optionally execute the click function and expand to the selected node
  selectNodeByIdAndPath: (nodeListId, nodeId, nodePath, executeClickFunction = true, expandToSelected = true, collapseOtherNodes = true) => {
    let nodeSelected = false;
    if (!nodeListControlFunctions[nodeListId] && nodeId && nodePath) {
      // Exact match doesn't exist, so check all nodes who's id starts with the nodeListId
      const selectedNode_multiple = {};
      Object.keys(nodeListControlFunctions).forEach(key => {
        if (key.startsWith(nodeListId) && nodeListControlFunctions[key] && nodeListControlFunctions[key].selectNodeByIdAndPath) {
          const selectedNode = nodeListControlFunctions[key].selectNodeByIdAndPath(nodeId, nodePath, executeClickFunction, expandToSelected, collapseOtherNodes);
          if (selectedNode) {
            nodeSelected = true;
            selectedNode_multiple[key] = selectedNode;
          }
        }
      });
      if (nodeSelected) {
        return selectedNode_multiple;
      }
    }
    if (!nodeSelected && nodeListControlFunctions[nodeListId] && nodeListControlFunctions[nodeListId].selectNodeByIdAndPath) {
      if (nodeId && nodePath) {
        return nodeListControlFunctions[nodeListId].selectNodeByIdAndPath(nodeId, nodePath, executeClickFunction, expandToSelected, collapseOtherNodes);
      } else {
        console.warn(`Node data must contain id and path properties to select a node.`);
        return null;
      }
    } else {
      console.warn(`NodeList with id ${nodeListId} not found. Cannot select node.`);
      return null;
    }
  },
  // Sometimes we need to force the node list to fetch new sub-nodes for a specific node,
  // this is useful if we need them available before we have drilled down to them in the node list.
  getSubNodes: (nodeListId, node, addProps = null) => {
    // console.log("NODE_LIST_CONTROLLER.getSubNodes called for nodeListId:", nodeListId, "node:", node, "addProps:", addProps, nodeListControlFunctions);
    let subNodesHaveUpdated = false;
    if (!nodeListControlFunctions[nodeListId]) {
      // No exact match, so update all nodes who's id starts with the nodeListId
      const newSubNode_multiple = {};
      Object.keys(nodeListControlFunctions).forEach(key => {
        if (key.startsWith(nodeListId) && nodeListControlFunctions[key] && nodeListControlFunctions[key].getSubNodes) {
          const newSubNodes = nodeListControlFunctions[key].getSubNodes(node, nodeListControlFunctions[key].nodes);
          if (newSubNodes) {
            if (addProps) {
              // Add any additional properties to the new sub-nodes
              newSubNodes.forEach(subNode => {
                Object.assign(subNode, addProps);
              });
            }
            nodeListControlFunctions[key].compareAndUpdateSubNodes(node, newSubNodes);
            newSubNode_multiple[key] = newSubNodes;
            subNodesHaveUpdated = true;
          }
        }
      });
      if (subNodesHaveUpdated) {
        // console.log("NODE_LIST_CONTROLLER.getSubNodes updated multiple node lists:", newSubNode_multiple);
        return newSubNode_multiple;
      }
    }
    if (!subNodesHaveUpdated) {
      const newSubNodes = nodeListControlFunctions[nodeListId]?.getSubNodes(node, nodeListControlFunctions[nodeListId]?.nodes);
      if (newSubNodes) {
        if (addProps) {
          // Add any additional properties to the new sub-nodes
          newSubNodes.forEach(subNode => {
            Object.assign(subNode, addProps);
          });
        }
        nodeListControlFunctions[nodeListId]?.compareAndUpdateSubNodes(node, newSubNodes);
      }
      return newSubNodes;
    }
  }
}

const NodeList = (
  {
    id="node-list", // Unique identifier for the node list, used to access control functions
    // Node data structure:
    nodes = [
      {
        id: "node1", name: "Node 1", path: "node1", 
        enabled: true, selected: false, highlighted: false, active: false, expanded: false,
        hasChildren: true, dynamicSubNodes: false,
        subNodes: [
          { id: "subNode1", name: "Sub Node 1", path: "node1.subNode1", enabled: true, selected: false, hasChildren: true, dynamicSubNodes: false, expanded: false, subNodes: null }
        ]
      },
      { id: "node2", name: "Node 2", path: "node2", enabled: true, selected: false, hasChildren: true, dynamicSubNodes: false, expanded: false, subNodes: null },
      { id: "node3", name: "Node 3", path: "node3", enabled: true, selected: false, hasChildren: true, dynamicSubNodes: false, expanded: false, subNodes: null }
    ],

    indentation = "10px",
    expandNodeElement = <span>+</span>,
    collapseNodeElement = <span>-</span>,
    dragHandleElement = <span>::</span>,
    dropIndicatorElement = <div className="node-list_drag-drop-indicator"></div>,

    autoCollapseChildren = true,
    clickAnywhereToExpand = true,
    autoExpandSelectedChildren = true,
    showGhostWhileDragging = true,
    dragGhostOpacity = 0.6,
    dragMode = NODE_LIST_DRAG_MODES.INDICATED_DRAG,

    highlightParentNodeOnHover = false,

    // getSubNodes function to fetch sub-nodes
    // This is used when a node is set to have children, but no subNodes are provided
    // It should return an array of sub-nodes or null if there are no sub-nodes
    getSubNodes = (node, nodes) => console.log('Default getSubNodes: ', node, nodes),

    // renderNodeName function (completely optional) to render the node name
    // It receives the node object and should return a React element to render the node name (or return the original name if you don't want to do anything special)
    // Allows you to apply custom styles or components to the node name at render time
    // If not provided, the default node name will be rendered as a string
    // This is completely optional, you can simply set react element as the name property of the node object to achieve the same thing,
    // however, there are times when storing a react element in your data isn't practical, so this function allows you to render the node name dynamically.
    // The best way to do this is do add a new property to your node data that identifies how you need to render the node name.
    // The function should look like this:
    // renderNodeName = (nodeName, node, nodes, isExpandable) => <span className="custom-node-name">{nodeName}</span>
    // Notice the node name is passed as the first argument, this is needed to support dynamic nodes names.
    renderNodeName = null,

    // checkNodeVisibility function to determine if a node should be visible
    // It receives the node object and should return true if the node should be visible, or false if it should be hidden
    // If not provided, all nodes will be visible by default.
    // checkNodeVisibility = (node, nodes) => {
    //   // Example: only show nodes that are enabled, for icast we will use display conditions.
    //   return node.enabled;
    // }
    checkNodeVisibility = null,

    // onNodeClick function to handle node click events
    // It receives the clicked node, its path, and the full list of nodes, as well as the event
    onNodeClick = (e, node, nodes) => console.log('Default onNodeClick: ', e, node, nodes),

    // onNodeRightClick function to handle right click events
    // It receives the clicked node, its path, and the full list of nodes, as well as the event
    // After the right click has been handled, you can call the onClose callback to re-render the node list, 
    // making sure to pass the original clicked node back (we may have a new copy of this node in our list and will need to update the state).
    enableRightClickMenu = false,
    onNodeRightClick = (e, node, nodes, onDone) => {
      console.log('Default onNodeRightClick: ', e, node, nodes, onDone);
      // Return true to indicate the right click has been handled, we will not show the default right click menu
      // Make sure you call the onDone callback when you are done with the right click menu (or whatever else right clicking might do)
      return true;
    },

    // onNodeDragEnd function to handle drag end events
    // It receives the dragged node, its new index, the sub-nodes list, and the full list of nodes
    onNodeDragEnd = (node, newIndex, subNodesList, nodes) => console.log('Default onNodeDragEnd: ', node, newIndex, subNodesList, nodes),

    // onNodeRenamed function to handle node renaming events
    onNodeRenamed = (node, newName, nodes) => console.log('Default onNodeRenamed: ', node, newName, nodes),

    className = null,
    style = null,
  }
) => {

  // A way of forcing state updates to trigger a re-render
  const [updateState, setUpdateState] = useState(0);
  const nodesUpdated = () => {
    setUpdateState(prev => prev + 1);
  };

  const fakeClickEvent = {
    preventDefault: () => {},
    stopPropagation: () => {},
    target: {
      id: id + '_node-list',
      className: 'node-list',
      dataset: {
        id: id,
      }
    }
  };

  const [highlightedParentNode, setHighlightedParentNode] = useState(null);

  // Renaming a node in the node list
  const [renameNodeId, setRenameNodeId] = useState(null);
  const [renameNodePath, setRenameNodePath] = useState(null);
  const renameNode = (id, path) => {
    // We can only rename a node if it is currently visible (ie. it is a child of an expanded node)
    const node = findNodeByIdAndPath(id, path, nodes);
    if (node && isNodeVisible(node, nodes)) {
      setRenameNodeId(id);
      setRenameNodePath(path);
      nodesUpdated();
    }
  }

  // Select a node by id and path, this is used to select a node from outside the component, optionally expand to the selected node
  const selectNodeByIdAndPath = (id, path, executeClickFunction = true, expandToSelected = true, collapseOtherNodes = true) => {
    const node = findNodeByIdAndPath(id, path, nodes);
    if (node) {
      deselectAllNodes(nodes);
      node.selected = true;
      if (expandToSelected) {
        if (collapseOtherNodes) {
          // Collapse all other nodes if requested
          collapseAllNodes(nodes);
        }
        for (let i = 0; i < nodes.length; i++) {
          if (hasSelectedChildren(nodes[i])) {
            expandToSelectedChildElement(nodes[i]);
          }
        }
      }
      if (executeClickFunction && onNodeClick) {
        // If we have a click function, execute it with the node and nodes list
        onNodeClick(fakeClickEvent, node, nodes);
      }
      nodesUpdated();
      return node; // Return the selected node
    } else {
      console.warn(`Node with id ${id} and path ${path} not found in the node list.`);
      return null; // Return null if the node was not found
    }
  }

  const compareAndUpdateSubNodes = (node, newSubNodes) => {
    // Compare the existing subNodes with the newSubNodes
    // Any nodes with matching ids are updated, preserving expanded and selected states
    // Nodes that no longer exist in newSubNodes are removed
    // Nodes that are inserted are inserted into subNode list with default states
    if (!node.subNodes) {
      node.subNodes = [];
    }
    const existingSubNodeIds = new Set(node.subNodes.map(subNode => subNode.id));
    const updatedSubNodes = [];
    newSubNodes.forEach(newSubNode => {
      if (existingSubNodeIds.has(newSubNode.id)) {
        // If the subNode already exists, update it
        const existingSubNode = node.subNodes.find(subNode => subNode.id === newSubNode.id);
        updatedSubNodes.push({
          ...existingSubNode,
          ...newSubNode,
          // Preserve existing subNodes
          subNodes: existingSubNode.subNodes || []
        });
      } else {
        // If the subNode is new, add it with default states
        updatedSubNodes.push({
          ...newSubNode,
        });
      }
    });
    // Remove any subNodes that no longer exist in newSubNodes
    node.subNodes = updatedSubNodes.filter(subNode => newSubNodes.some(newSubNode => newSubNode.id === subNode.id));
    
    // Our node list is now update, but we need to ensure that the subNodes are also updated.
    // The subNodeData is already present in newSubNodes, so we can just update the subNodes of each node
    if (node.subNodes && node.subNodes.length > 0) {
      node.subNodes.forEach(subNode => {
        const newSubNode = newSubNodes.find(n => n.id === subNode.id);
        if (newSubNode) {
          compareAndUpdateSubNodes(subNode, newSubNode.subNodes || []);
        }
      });
    }
  }

  if (nodeListControlFunctions[id]) {
    // console.warn(`NodeList with id ${id} already exists. Overwriting control functions.`);
    nodeListControlFunctions[id].nodesUpdated = nodesUpdated;
    nodeListControlFunctions[id].renameNode = renameNode;
    nodeListControlFunctions[id].selectNodeByIdAndPath = selectNodeByIdAndPath;
    nodeListControlFunctions[id].getSubNodes = getSubNodes;
  } else {
    nodeListControlFunctions[id] = {
      nodesUpdated: nodesUpdated,
      renameNode: renameNode,
      selectNodeByIdAndPath: selectNodeByIdAndPath,
      getSubNodes: getSubNodes,
      nodes: nodes,
      compareAndUpdateSubNodes: compareAndUpdateSubNodes,
    };
  }
  
  const handleExpandToggleClick = (node, nodes) => {
    console.log("Toggling expand for node:", node);
    const gotChildren = node.hasChildren && node.subNodes && node.subNodes.length > 0;
    if (gotChildren) {
      node.expanded = !node.expanded;
      if (autoExpandSelectedChildren && node.expanded) {
        expandToSelectedChildElement(node);
      }

      if (!node.expanded && autoCollapseChildren) {
        const collapseChildren = (nodesList) => {
          nodesList.forEach(childNode => {
            childNode.expanded = false;
            if (childNode.subNodes && childNode.subNodes.length > 0) {
              collapseChildren(childNode.subNodes);
            }
          });
        }
        if (node.subNodes && node.subNodes.length > 0) {
          collapseChildren(node.subNodes);
        }
      }

      nodesUpdated();
    } else {
      node.expanded = false;
      nodesUpdated();
    }
  };

  const handleNodeClick = (e, node, nodes) => {
    console.log("Node clicked:", node);
    const isSelected = onNodeClick ? onNodeClick(e, node, nodes) : false;
    if (clickAnywhereToExpand && !node.expanded) {
      expandToSelectedChildElement(node);
      nodesUpdated();
    }
    if (isSelected) {
      deselectAllNodes(nodes);
      node.selected = true;
      nodesUpdated();
    } else {
      node.selected = false;
      nodesUpdated();
    }
  };

  const handleRightClick = (e, node, nodes) => {
    if (onNodeRightClick) {
      const handled = onNodeRightClick(e, node, nodes, 
        () => {
          // The right click action has completed, we can now reset the active state of the node.
          // However, because this callback isn't necessarily called immediately, the node list may have updated
          // meaning the node may not be the same object referenced in the list.
          // So we need to find the matching node in the list and update its state (id and path should still match).
          node.active = false; // Reset active state after right click
          findNodeAndUpdateState(node);
          nodesUpdated();
        }
      );
      if (handled) {
        // If the right click has been handled, we do not show the default right click menu
        e.preventDefault();
        e.stopPropagation();
        node.active = true; // Set the node as active to indicate it was right clicked
      }
    }
    nodesUpdated();
  }

  const [dragAllowedForElementId, setDragAllowedForElementId] = useState(null);
  const [draggingNode, setDraggingNode] = useState(null);
  const [draggingNodeHeight, setDraggingNodeHeight] = useState(0); // Height of the dragging node, if needed
  const [draggingNodeParentList, setDraggingNodeParentList] = useState(null);

  // Ref version are needed of these for our mousemove handler as it can't access the state directly
  const draggingNodeRef = useRef(null);
  const draggingNodeHeightRef = useRef(0);
  const draggingNodeParentListRef = useRef(null);

  // Ghost clone is a clone of the dragging node element that follows the mouse cursor while dragging
  const [draggingNodeElementGhostClone, setDraggingNodeElementGhostClone] = useState(null);
  const draggingNodeElementGhostCloneRef = useRef(null);
  const [ghostCloneMouseOffset, setGhostCloneMouseOffset] = useState({ x: 0, y: 0 });
  const ghostCloneMouseOffsetRef = useRef({ x: 0, y: 0 });

  // Drop index is only used when we are using the indicated drag mode
  const [dragDropIndex, setDragDropIndex] = useState(null);
  const dragDropIndexRef = useRef(null);

  const startDrag = (event, node, nodeElementId, parentList) => {
    event.stopPropagation();

    if (dragAllowedForElementId !== nodeElementId) {
      // If the drag is not allowed for this element, do not start dragging
      // This happens when the user hovers over the drag handle element
      return;
    } 

    setDraggingNode(node);
    draggingNodeRef.current = node; 
    setDraggingNodeParentList(parentList || null);
    draggingNodeParentListRef.current = parentList || null;

    setDragDropIndex(null);
    dragDropIndexRef.current = null;

    // The element height we are using here is the unexpanded height of the node element
    // We can use this to calculate the position of the ghost clone while dragging
    // and also to determine the drop position in the list
    const draggingNodeElement = document.getElementById(nodeElementId + '_node-item');
    if (draggingNodeElement) {
      const draggingNodeElementClientRect = draggingNodeElement.getBoundingClientRect();
      setDraggingNodeHeight(draggingNodeElementClientRect.height);
      draggingNodeHeightRef.current = draggingNodeElementClientRect.height;
    } else {
      setDraggingNodeHeight(0);
      draggingNodeHeightRef.current = 0;
    }

    if (showGhostWhileDragging) {
      // Create a clone of the node element to use as a ghost clone while dragging
      const draggingFullElement = draggingNodeElement;
      const draggingNodeElementClone = draggingFullElement.cloneNode(true);
      const ghostCloneMouseOffset = {
        x: event.clientX - draggingFullElement.getBoundingClientRect().left,
        y: event.clientY - draggingFullElement.getBoundingClientRect().top
      };
      draggingNodeElementClone.id = nodeElementId + '_dragging-clone'; // Set a new ID for the clone
      draggingNodeElementClone.style.position = 'fixed'; // Make it fixed position so it follows the mouse
      draggingNodeElementClone.style.pointerEvents = 'none'; // Disable pointer events on the clone
      draggingNodeElementClone.style.zIndex = '1000000'; // Set a high z-index to make sure it is on top
      draggingNodeElementClone.style.opacity = dragGhostOpacity; // Make it semi-transparent
      draggingNodeElementClone.style.left = (event.clientX - ghostCloneMouseOffset.x) + 'px'; // Set the left position to the mouse position
      draggingNodeElementClone.style.top = (event.clientY - ghostCloneMouseOffset.y) + 'px'; // Set the top position to the mouse position
      draggingNodeElementClone.style.width = draggingFullElement.offsetWidth + 'px'; // Set the width of the clone to match the original element
      draggingNodeElementClone.style.height = draggingFullElement.offsetHeight + 'px'; // Set the height of the clone to match the original element
      draggingNodeElementClone.style.paddingLeft = `calc(${node.depth} * ${indentation} + 0.5rem)`
      // Add the dragging class to the clone
      draggingNodeElementClone.classList.add('node-list_node-item_dragging'); // Add the dragging class to the clone
      document.body.appendChild(draggingNodeElementClone); // Append the clone to the body

      setDraggingNodeElementGhostClone(draggingNodeElementClone); // Set the ghost clone in state
      draggingNodeElementGhostCloneRef.current = draggingNodeElementClone; // Store the ghost clone in the ref
      setGhostCloneMouseOffset(ghostCloneMouseOffset); // Set the ghost clone mouse offset in state
      ghostCloneMouseOffsetRef.current = ghostCloneMouseOffset; // Store the ghost clone mouse offset in the ref
    }

    // Add event listenders and a style to the body while dragging
    document.addEventListener('mouseup', endDrag);
    document.body.classList.add('node-list_dragging-cursor');
    document.addEventListener('mousemove', processDragPosition); 
  }
  const endDrag = (event) => {
    event.stopPropagation();

    // Remove the event listeners and styles from the body
    document.removeEventListener('mouseup', endDrag); 
    document.body.classList.remove('node-list_dragging-cursor');
    document.removeEventListener('mousemove', processDragPosition);

    // Remove the ghost clone from the body
    if (draggingNodeElementGhostClone || draggingNodeElementGhostCloneRef.current) {
      const draggingNodeElementClone = draggingNodeElementGhostClone || draggingNodeElementGhostCloneRef.current;
      draggingNodeElementClone.remove();
      setDraggingNodeElementGhostClone(null);
      draggingNodeElementGhostCloneRef.current = null;
    }

    // In indicated mode we need to modify the node list now, since it isn't done while dragging
    if (dragMode === NODE_LIST_DRAG_MODES.INDICATED_DRAG) {
      const dragDropIndex_internal = dragDropIndexRef.current;
      if (dragDropIndex_internal !== null) {
        const draggingNode_internal = draggingNodeRef.current;
        const draggingNodeParentList_internal = draggingNodeParentListRef.current;
        if (draggingNode_internal && draggingNodeParentList_internal) {
          const currentNodeIndex = draggingNodeParentList_internal.findIndex(n => n.id === draggingNode_internal.id);
          if (currentNodeIndex !== dragDropIndex_internal) {
            // If moving up the list we need to remove the node from its current position before inserting it
            if (currentNodeIndex !== -1 && currentNodeIndex > dragDropIndex_internal) {
              draggingNodeParentList_internal.splice(currentNodeIndex, 1);
            }
            // Insert the dragging node at the new drop index
            if (dragDropIndex_internal < draggingNodeParentList_internal.length) {
              draggingNodeParentList_internal.splice(dragDropIndex_internal, 0, draggingNode_internal);
            } else {
              draggingNodeParentList_internal.push(draggingNode_internal);
            }
            // If moving down the list we need to remove the node from its current position after inserting it
            if (currentNodeIndex !== -1 && currentNodeIndex < dragDropIndex_internal) {
              draggingNodeParentList_internal.splice(currentNodeIndex, 1);
            }
            nodesUpdated();
          }
        }
      }
    }

    // Report the result back via the callback function
    if (draggingNodeRef.current && onNodeDragEnd) {
      const nodeList = draggingNodeParentListRef.current || nodes;
      const newIndex = nodeList.findIndex(n => n.id === draggingNodeRef.current.id);
      if (newIndex !== -1) {
        onNodeDragEnd(draggingNodeRef.current, newIndex, nodeList, nodes);
      }
    }

    setDraggingNode(null);
    draggingNodeRef.current = null;
    setDragAllowedForElementId(null);
    setDraggingNodeParentList(null);
    draggingNodeParentListRef.current = null;
    setDraggingNodeHeight(0);
    draggingNodeHeightRef.current = 0;
  }

  // Process the mouse position while dragging
  // The result is determined by the drag mode:
  // - In live drag mode, the nodes are reordered live as the mouse moves
  // - In indicated drag mode, we just save the drop index and use a line to indicate where the node will be dropped
  const processDragPosition = (event) => {
    // Nodes that are draggable can be reordered by dragging, but only within the same list (not subNodes or parents)
    // If the mouse position is over a node, our draggingNode will be moved to that position
    if (!draggingNode && !draggingNodeRef.current) return;

    const draggingNode_internal = draggingNode || draggingNodeRef.current;
    const draggingNodeHeight_internal = draggingNodeHeight || draggingNodeHeightRef.current;

    const mouseY = event.clientY;
    const ghostCloneMouseOffset_loc = ghostCloneMouseOffsetRef.current || ghostCloneMouseOffset;
    const offsetMouseY = mouseY - ghostCloneMouseOffset_loc.y;
    let draggingElementMiddleY = offsetMouseY + draggingNodeHeight_internal / 2;
    
    const mouseX = event.clientX;

    // If we have a ghost clone, update its position to follow the mouse
    if (showGhostWhileDragging && (draggingNodeElementGhostClone || draggingNodeElementGhostCloneRef.current)) {
      const draggingNodeElementClone = draggingNodeElementGhostClone || draggingNodeElementGhostCloneRef.current;
      draggingNodeElementClone.style.left = (mouseX - ghostCloneMouseOffset_loc.x) + 'px'; // Update the left position of the clone to follow the mouse
      draggingNodeElementClone.style.top = (mouseY - ghostCloneMouseOffset_loc.y) + 'px'; // Update the top position of the clone to follow the mouse
    }

    let moved = false;
    const nodeList = (draggingNodeParentList ?? draggingNodeParentListRef.current) || nodes;
    const draggingNodeElementId = draggingNode_internal.id + '_' + draggingNode_internal.path;

    let newIndicatedDragDropIndex = null;

    nodeList.forEach(node => {
      if (!moved) {
        const elementId = node.id + '_' + node.path;
        if (elementId !== draggingNodeElementId || dragMode === NODE_LIST_DRAG_MODES.INDICATED_DRAG) {
          const element = document.getElementById(elementId);
          if (element) {
            const rect = element.getBoundingClientRect();
            const draggingNodeIndex = dragMode === NODE_LIST_DRAG_MODES.LIVE_DRAG ? nodeList.findIndex(n => n.id === draggingNode_internal.id) : dragDropIndexRef.current; // Get the index of the dragging node in the node list, or the drop index if in indicated drag mode
            const nodeIndex = nodeList.findIndex(n => n.id === node.id);
            // Check if the mouse is over the element
            if (dragMode === NODE_LIST_DRAG_MODES.LIVE_DRAG) {
              if (
                (nodeIndex <= draggingNodeIndex - 1 && draggingElementMiddleY > rect.top && draggingElementMiddleY < rect.top + draggingNodeHeight_internal / 2) ||
                (nodeIndex >= draggingNodeIndex + 1 && draggingElementMiddleY < rect.bottom && draggingElementMiddleY > rect.bottom - draggingNodeHeight_internal / 2)
              ) {
                if (draggingNodeIndex !== -1) {
                  // Remove the dragging node from its current position
                  const draggingNodeIndex = nodeList.findIndex(n => n.id === draggingNode_internal.id);
                  const [draggedNode] = nodeList.splice(draggingNodeIndex, 1);
                  // Insert it in its new position
                  if (nodeIndex !== -1) {
                    nodeList.splice(nodeIndex, 0, draggedNode);
                    nodesUpdated();
                    moved = true;
                  }
                }
              }
            } else if (dragMode === NODE_LIST_DRAG_MODES.INDICATED_DRAG) {
              if (draggingElementMiddleY >= rect.top - draggingNodeHeight_internal / 2 && draggingElementMiddleY <= rect.top + draggingNodeHeight_internal / 2) {
                // Moving up the list, so we set the drop index to the current node index
                newIndicatedDragDropIndex = nodeIndex;
                moved = true;
              } else if (draggingElementMiddleY >= rect.bottom - draggingNodeHeight_internal / 2 && draggingElementMiddleY <= rect.bottom + draggingNodeHeight_internal / 2) {
                // Moving down the list, so we set the drop index to the next node index
                newIndicatedDragDropIndex = nodeIndex + 1;
                moved = true;
              }
            }
          }
        }
      }
    });

    if (moved && dragMode === NODE_LIST_DRAG_MODES.INDICATED_DRAG) {
      const originalNodeIndex = nodeList.findIndex(n => n.id === draggingNode_internal.id);
      if (originalNodeIndex !== -1) {
        // Make sure we don't indicate the same index as the original node index
        if (originalNodeIndex === newIndicatedDragDropIndex || originalNodeIndex === newIndicatedDragDropIndex - 1) {
          newIndicatedDragDropIndex = null;
        }
        setDragDropIndex(newIndicatedDragDropIndex);
        dragDropIndexRef.current = newIndicatedDragDropIndex;
      }
    }
  }

  const deselectAllNodes = (nodesList) => {
    nodesList.forEach(node => {
      node.selected = false;
      if (node.subNodes && node.subNodes.length > 0) {
        deselectAllNodes(node.subNodes); // Recursively deselect children
      }
    });
  };

  const hasSelectedChildren = (node) => {
    if (!node.hasChildren || !node.subNodes || node.subNodes.length === 0) return false;
    return node.subNodes.some(subNode => subNode.selected || hasSelectedChildren(subNode));
  };

  const hasHighlightedChildren = (node) => {
    if (!node.hasChildren || !node.subNodes || node.subNodes.length === 0) return false;
    return node.subNodes.some(subNode => subNode.highlighted || hasHighlightedChildren(subNode));
  };

  const collapseAllNodes = (nodesList) => {
    nodesList.forEach(node => {
      node.expanded = false;
      if (node.subNodes && node.subNodes.length > 0) {
        collapseAllNodes(node.subNodes); // Recursively collapse children
      }
    });
  };

  const expandToSelectedChildElement = (node) => {
    node.expanded = true;
    // If the node has selected children, expand it
    if (hasSelectedChildren(node)) {
      // Go through the subNodes and expand them if they have selected children
      if (node.subNodes && node.subNodes.length > 0) {
        node.subNodes.forEach(subNode => {
          if (subNode.selected || hasSelectedChildren(subNode)) {
            expandToSelectedChildElement(subNode); // Recursively expand to selected elements
          } else {
            subNode.expanded = false;
          }
        });
      }
    }
  }

  const findNodeByIdAndPath = (id, path, nodesList) => {
    // console.log(`Searching for node with id: ${id} and path: ${path} in`, nodesList);
    for (let node of nodesList) {
      if (node.id === id && node.path === path) {
        return node;
      }
      if (node.hasChildren && node.subNodes && node.subNodes.length > 0) {
        const foundNode = findNodeByIdAndPath(id, path, node.subNodes);
        if (foundNode) {
          return foundNode;
        }
      }
    }
    return null; // Node not found
  }

  const getDynamicNodeName = (node) => {
    const linkedNode = findNodeByIdAndPath(node.dynamicName.id, node.dynamicName.path, nodes);
    if (linkedNode && linkedNode.subNodes && linkedNode.subNodes.length > 0) {
      const linkIndex = node.dynamicLinkedIndex;
      if (linkIndex >= 0 && linkIndex < linkedNode.subNodes.length) {
        return linkedNode.subNodes[linkIndex].name; // Return the name of the linked subNode
      }
    }
    return node.name;
  }


  // Check if the node is visible in the current list
  // We need to recursively check each expanded node looking for the node, when we find it return true
  const isNodeVisible = (node, nodeList) => {
    let nodeIsVisible = false;
    for (let listNode of nodeList) {
      if (listNode.id === node.id && listNode.path === node.path) {
        nodeIsVisible = true;
        break;
      }
      if (listNode.hasChildren && listNode.expanded && listNode.subNodes && listNode.subNodes.length > 0) {
        nodeIsVisible = nodeIsVisible || isNodeVisible(node, listNode.subNodes);
      }
    }
    return nodeIsVisible;
  }

  // Ensure our list nodes match the state of a node that has been passed in.
  // We use this when passing nodes back from the right click menu, as our node list may have been updated
  // and we need to ensure the state of the node is updated in the list to match any changes in the node that was passed in.
  const findNodeAndUpdateState = (node) => {
    const listNode = findNodeByIdAndPath(node.id, node.path, nodes);
    if (listNode) {
      // Update the state of the node in the list
      listNode.enabled = node.enabled;
      listNode.selected = node.selected;
      listNode.highlighted = node.highlighted;
      listNode.active = node.active;
      listNode.expanded = node.expanded;
    }
  }

  const RenameInput = ({ node }) => {
    const inputRef = useRef(null);

    useEffect(() => {
      if (inputRef.current) {
        inputRef.current.select();
      }
    }, []);

    return (
      <input
        ref={inputRef}
        type="text"
        className="node-list_node-item_rename-input"
        defaultValue={node.name}
        onBlur={(e) => {
          const newName = e.target.value.trim();
          if (newName) {
            // Update the node name in the list
            node.name = newName;
            findNodeAndUpdateState(node);
            nodesUpdated();
            if (onNodeRenamed) {
              onNodeRenamed(node, newName, nodes);
            }
          }
          setRenameNodeId(null);
          setRenameNodePath(null);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.target.blur(); // Trigger blur to save the name
          } else if (e.key === 'Escape') {
            setRenameNodeId(null);
            setRenameNodePath(null);
          }
        }}
        autoFocus
        placeholder={node.name}
      />
    )
  }

  const Nodes = ({parent, nodeList, depth}) => {
    if (!nodeList || nodeList.length === 0) return null;
    const indicateDraggingInThisList = draggingNode && dragMode === NODE_LIST_DRAG_MODES.INDICATED_DRAG && draggingNodeParentList && nodeList === draggingNodeParentList;

    return (
      <div className={`node-list_sub-list${draggingNode ? ' node-list_no-mouse-events' : ''}`}>
        {nodeList.map(
          (node, nodeIndex, nodeArray) => {
            // If the node has children but no subNodes, fetch them using getSubNodes
            if (node.hasChildren && node.dynamicSubNodes) {
              const subNodes = getSubNodes ? getSubNodes(node, nodes) : node.subNodes;
              if (subNodes && subNodes.length > 0) {
                compareAndUpdateSubNodes(node, subNodes);
              } else {
                node.subNodes = [];
                node.expanded = false;
              }
            } else if (!node.hasChildren) {
              node.subNodes = [];
              node.expanded = false;
            }

            // We want to make it as easy as possible for external code to access the node's depth and parent...
            node.depth = depth;   // Add depth to the node so we can look that up when processing clicks
            node.parent = parent; // Add parent reference to the node for easier access when processing clicks

            const gotChildren = node.hasChildren && node.subNodes && node.subNodes.length > 0;
            const elementId = node.id + '_' + node.path;
            const rawNodeName = node.dynamicName ? getDynamicNodeName(node) : node.name;

            if (checkNodeVisibility && !checkNodeVisibility(node, nodes)) {
              return null; // If we have a visibility function and it returns false for this node, do not render it
            }

            return (
              <Fragment key={elementId + '_' + depth + '_' + nodeIndex}>
                {indicateDraggingInThisList && dragDropIndex === 0 && nodeIndex === 0 &&
                  <>{dropIndicatorElement}</>
                }
                <div
                  id={elementId}
                  className={`node-list_node-item${gotChildren && node.expanded && depth === 0 ? ' node-list_node-item_expanded' : ''}${node.selected || (!node.expanded && hasSelectedChildren(node)) ? ' node-list_node-item_selected' : ''}${draggingNode && draggingNode.id === node.id ? ' node-list_node-item_dragging' : ''}${node === highlightedParentNode ? ' node-list_parent-node_hilighted' : ''}`}
                >
                  <div 
                    id={elementId + '_node-item'}
                    className={`node-list_node-item_node${node.active ? ' node-list_node-item_active' : ''}`}
                    style={{ paddingLeft: `calc(${depth} * var(--indentation, 0) + 0.5rem)` }}
                    onMouseEnter={highlightParentNodeOnHover ? () => setHighlightedParentNode(node.parent) : null}
                    onMouseLeave={highlightParentNodeOnHover ? () => setHighlightedParentNode(null) : null}
                  >
                    {gotChildren && node.enabled ? 
                      <div className="node-list_node-item_expander" onClick={(e) => {e.stopPropagation(); handleExpandToggleClick(node, nodes)}}>{(node.expanded ? <>{collapseNodeElement}</> : <>{expandNodeElement}</>)}</div> 
                      : ''
                    }
                    {renameNodeId === node.id && renameNodePath === node.path &&
                      <RenameInput node={node} />
                    }
                    {(renameNodeId !== node.id || renameNodePath !== node.path) &&
                      <>
                        <div className={`node-list_node-item_name${node.enabled ? '' : ' node-list_node-item_disabled'}${node.clickable === false ? ' node-list_node-item_unclickable' : ''}${node.highlighted || (!node.expanded && hasHighlightedChildren(node)) ? ' node-list_node-item_highlighted' : ''}`} onClick={(e) => {e.stopPropagation(); node.enabled && node.clickable !== false && handleNodeClick(e, node, nodes);}} onContextMenu={enableRightClickMenu ? (e) => handleRightClick(e, node, nodes) : null}>
                          {renderNodeName ? renderNodeName(rawNodeName, node, nodes, (gotChildren && node.enabled)) : rawNodeName}
                        </div>
                      </>
                    }
                    {node.draggable &&
                      <>
                        <div className="node-list_node-item_draggable" onMouseOver={(e) => setDragAllowedForElementId(elementId)} onMouseOut={(e) => setDragAllowedForElementId(null)} onMouseDown={node.draggable && dragAllowedForElementId === elementId ? (e) => startDrag(e, node, node.id + '_' + node.path, nodeList) : null}>{dragHandleElement}</div>
                      </>
                    }
                  </div>
                  {gotChildren && node.expanded && (
                    <Nodes parent={node} nodeList={node.subNodes} depth={depth + 1} />
                  )}
                </div>
                {indicateDraggingInThisList && dragDropIndex === nodeIndex + 1 &&
                  <>{dropIndicatorElement}</>
                }
              </Fragment>
            )
          }
        )}
      </div>
    );
  }

  const styleObject = style ? { ...style, "--indentation": indentation } : { "--indentation": indentation };
  return (
    <div className={`node-list-container${className ? ' ' + className : ''}`} style={styleObject}>
      <Nodes parent={null} nodeList={nodes} depth={0} />
    </div>
  );
}

export default NodeList;
